import type { ColumnDef } from "@tanstack/react-table";
import { capitalize, highlightText } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { VatRate } from "~/providers/general-provider";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { useGeneral } from "~/hooks/use-general";
import { useVatRateManager } from "~/managers/use-vat-rate-manager";

export const Route = createFileRoute("/_main/settings/_layout/vat-rates")({
  component: VatRates,
});

function VatRates() {
  const { t } = useTranslation("common");
  const { openVatRateForm } = useVatRateManager();
  const { vatRates } = useGeneral();
  const columns: ColumnDef<VatRate>[] = [
    {
      id: t("name"),
      accessorKey: "name",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      cell: ({ row: { original: vatRate }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(vatRate.name, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("VatRate.value"),
      accessorKey: "value",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("VatRate.value")} />
      ),
      cell: ({ row: { original: vatRate }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(`${vatRate.value}%`, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
  ];

  return (
    <>
      <SettingsHeader
        title={t("vat_rates")}
        buttonLabel={capitalize(t("new_vat_rate"))}
        buttonAction={async () => {
          await openVatRateForm();
        }}
      />

      <DataTable
        defaultSorting={[{ id: t("VatRate.value"), desc: false }]}
        onSelect={openVatRateForm}
        columns={columns}
        data={vatRates}
      />
    </>
  );
}
